import { useMemo, useState } from 'react'
import { diffDays } from '../../shared/date'
import type { Prediction } from '../../shared/predict'
import { LogSheet } from '../components/LogSheet'
import { Button, Card, SectionTitle } from '../components/ui'
import { useI18n, type Dict } from '../i18n'
import { useReady } from '../store'

/**
 * 伴侣视角。
 *
 * 和首页看的是同一份数据，但问题不一样：首页回答「我现在处于周期哪个阶段」，
 * 这一页回答「今天该怎么对 TA」。所以不堆数字，只给一句能直接照做的话。
 *
 * 只在有两个人的时候才出现（见 App.tsx）—— 一个人用的时候，
 * 一整页「TA 今天怎么样」指的就是自己，纯属添乱。
 */
export function Us() {
  const { t, formatDate } = useI18n()
  const { snapshot, prediction } = useReady()
  const [sheetOpen, setSheetOpen] = useState(false)

  // 用记录方的昵称，而不是写死「她」—— 记录周期的不一定是女性伴侣，
  // 也可能是同性伴侣，或者干脆是自己在两台设备上看
  const trackerName =
    snapshot.members.find((m) => m.role === 'tracker')?.displayName ?? t.us.trackerFallback

  const timeline = useMemo(() => {
    const items: { date: string; kind: 'intimacy' | 'period' | 'symptom'; text: string }[] = []
    for (const i of snapshot.intimacy) {
      items.push({ date: i.date, kind: 'intimacy', text: t.protection[i.protection] })
    }
    for (const p of snapshot.periods) {
      items.push({ date: p.date, kind: 'period', text: t.flow[p.flow] })
    }
    for (const s of snapshot.symptoms) {
      if (s.symptoms.length === 0) continue
      items.push({
        date: s.date,
        kind: 'symptom',
        text: s.symptoms
          .map((k) => t.symptom[k as keyof typeof t.symptom] ?? k)
          .join(t.meta.listSep),
      })
    }
    return items.sort((a, b) => (a.date < b.date ? 1 : -1)).slice(0, 12)
  }, [snapshot, t])

  return (
    <div className="px-4 pb-6 space-y-5">
      <header className="safe-top pb-1 px-1">
        <h1 className="text-2xl font-semibold">{t.us.title}</h1>
        <p className="text-sm text-muted mt-1">{snapshot.settings.name}</p>
      </header>

      <Card className="bg-accent/8">
        <p className="text-xs text-muted mb-2">{t.us.statusOf(trackerName)}</p>
        <p className="text-xl font-semibold">
          {t.phase[prediction.phase]}
          {prediction.cycleDay !== null && (
            <span className="text-sm font-normal text-muted ml-2">
              {t.home.cycleDay(prediction.cycleDay)}
            </span>
          )}
        </p>
      </Card>

      <div>
        <SectionTitle>{t.us.tips}</SectionTitle>
        <Card>
          <p className="text-[15px] leading-relaxed">{tipFor(t, prediction)}</p>
          {prediction.nextPeriod && (
            <p className="text-[13px] text-muted mt-3 pt-3 border-t border-line/60 leading-relaxed">
              {t.home.expectedRange(
                formatDate(prediction.nextPeriod.earliest),
                formatDate(prediction.nextPeriod.latest),
              )}
            </p>
          )}
        </Card>
      </div>

      <Button variant="soft" className="w-full" onClick={() => setSheetOpen(true)}>
        {t.home.logToday}
      </Button>

      <div>
        <SectionTitle>{t.us.timeline}</SectionTitle>
        <Card>
          {timeline.length === 0 ? (
            <p className="text-sm text-muted py-4 text-center">{t.us.noRecords}</p>
          ) : (
            <ul className="space-y-3">
              {timeline.map((item, idx) => (
                <li key={`${item.date}-${item.kind}-${idx}`} className="flex items-start gap-3">
                  <span
                    className={`w-2 h-2 rounded-full mt-2 shrink-0 ${
                      item.kind === 'period'
                        ? 'bg-period'
                        : item.kind === 'intimacy'
                          ? 'bg-fertile'
                          : 'bg-muted/50'
                    }`}
                  />
                  <div className="min-w-0 flex-1 flex items-baseline justify-between gap-3">
                    <span className="text-[15px] truncate">{item.text}</span>
                    <span className="text-xs text-muted shrink-0">
                      {t.us.daysAgo(diffDays(item.date, snapshot.today))}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <LogSheet date={snapshot.today} open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </div>
  )
}

function tipFor(t: Dict, p: Prediction): string {
  switch (p.phase) {
    case 'pms':
      return t.us.tipPms
    case 'menstrual':
      return t.us.tipMenstrual(p.cycleDay ?? 1)
    case 'fertile':
      return t.us.tipFertile
    case 'ovulation':
      return t.us.tipOvulation
    case 'late':
      return t.us.tipLate
    default:
      return t.us.tipNormal
  }
}
